import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { syncSsoReturnUrlHintWithToken } from '@/shared/lib/sso-return-url-hint';

interface TokenStore {
  accessToken: string | null;
  switchAccessToken: string | null;
}

interface TokenStoreActions {
  setAccessToken: (accessToken: string) => void;
  setSwitchAccessToken: (token: string | null) => void;
  clearSwitchAccount: () => void;
  logout: () => void;
}

const ACCOUNT_ID_KEY = 'forced_logout_account_id';

/**
 * JWT access token payload에서 accountId(sub)를 추출하여 localStorage에 저장
 * 강제 로그아웃 시 Zustand가 비어있어도 accountId를 확보하기 위함
 */
const persistAccountId = (accessToken: string): void => {
  try {
    const parts = accessToken.split('.');
    if (!parts[1]) return;
    const payload = JSON.parse(atob(parts[1]));
    const accountId = payload.sub;
    if (accountId) {
      localStorage.setItem(ACCOUNT_ID_KEY, String(accountId));
    }
  } catch {
    // JWT 파싱 실패 — 무시
  }
};

export const useTokenStore = create<TokenStore & TokenStoreActions>()(
  devtools(
    (set) => ({
      accessToken: null,
      switchAccessToken: null,
      setAccessToken: (accessToken: string) => {
        if (typeof window !== 'undefined') {
          persistAccountId(accessToken);
          syncSsoReturnUrlHintWithToken(accessToken); // ★ 유일한 삭제 트리거
        }
        set({ accessToken });
      },
      setSwitchAccessToken: (token: string | null) =>
        set({ switchAccessToken: token }),
      clearSwitchAccount: () => set({ switchAccessToken: null }),
      logout: () => set({ accessToken: null, switchAccessToken: null }),
    }),
    {
      name: 'AuthTokenStore',
      enabled: process.env.NEXT_PUBLIC_NODE_ENV !== 'production',
    },
  ),
);
